import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { useIdentity, usePayment } from '$api-clients/hooks';
import { useCompensationReportFilters } from '../../CompensationReportFilters/CompensationReportFilters.helpers';
import { buildReportingSummaryQuery, useViewReportAs } from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import type { ReportingSummaryQuery } from '../../types';
import {
  mapSummaryToPayoutBreakdownRow,
  type PayoutBreakdownTableRow,
} from '../PayoutBreakdown.helpers';

const DEFAULT_PAGE_SIZE = 10;

interface UseGetPayoutBreakdownProps {
  sortModel: GridSortModel;
  groupedBy: 'agent' | 'team';
}

interface UseGetPayoutBreakdownResult {
  rows: PayoutBreakdownTableRow[];
  loading: boolean;
  totalCount: number | undefined;
  rowCount: number | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
}

export { DEFAULT_PAGE_SIZE as PAYOUT_BREAKDOWN_DEFAULT_PAGE_SIZE };

export function useGetPayoutBreakdown({
  sortModel,
  groupedBy,
}: UseGetPayoutBreakdownProps): UseGetPayoutBreakdownResult {
  const { filters } = useCompensationReportFilters();
  const viewReportAs = useViewReportAs();
  const allTimeStartDate = useAllTimeStartDate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const isGroupedByAgent = groupedBy === 'agent';

  // Reset to page 0 when filters change. Returns the same object reference when
  // already on page 0 so React bails out without a re-render, preventing a
  // double fetch.
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [filters]);

  const baseQuery = useMemo(
    () =>
      viewReportAs
        ? buildReportingSummaryQuery(filters, viewReportAs, allTimeStartDate)
        : null,
    [filters, viewReportAs, allTimeStartDate],
  );

  const query = useMemo(() => {
    if (!baseQuery) return null;
    return {
      ...baseQuery,
      sortBy:
        (sortModel[0]?.field as NonNullable<ReportingSummaryQuery['sortBy']>) ||
        (isGroupedByAgent ? 'agent' : 'team'),
      sortDirection: sortModel[0]?.sort === 'asc' ? 'ASC' : 'DESC',
      groupBy: [isGroupedByAgent ? 'AGENT' : 'TEAM'],
      includePartiesWithoutTransactions: true,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
    };
  }, [baseQuery, sortModel, isGroupedByAgent, paginationModel]);

  const { data: summaryData, isLoading: isLoadingData } = usePayment(
    '/reporting/summary',
    query ? { params: { query: query as ReportingSummaryQuery } } : null,
  );

  const results = summaryData?.results ?? [];

  // Sort IDs for a stable SWR cache key regardless of result order.
  const userIds = useMemo(
    () =>
      isGroupedByAgent
        ? [
            ...new Set(
              results.flatMap((r) =>
                r.teamMemberIdentityId ? [r.teamMemberIdentityId] : [],
              ),
            ),
          ].sort()
        : [],
    [results, isGroupedByAgent],
  );
  const teamIds = useMemo(
    () =>
      !isGroupedByAgent
        ? [
            ...new Set(
              results.flatMap((r) =>
                r.teamIdentityId ? [r.teamIdentityId] : [],
              ),
            ),
          ].sort()
        : [],
    [results, isGroupedByAgent],
  );

  const { data: usersData, isLoading: isLoadingUsers } = useIdentity(
    '/users',
    userIds.length > 0
      ? { params: { query: { ids: userIds.join(',') } } }
      : null,
  );
  const { data: teamsData, isLoading: isLoadingTeams } = useIdentity(
    '/teams',
    teamIds.length > 0
      ? { params: { query: { ids: teamIds.join(',') } } }
      : null,
  );

  const usersById = useMemo(
    () => Object.fromEntries((usersData?.data ?? []).map((u) => [u.id, u])),
    [usersData],
  );
  const teamsById = useMemo(
    () => Object.fromEntries((teamsData?.data ?? []).map((t) => [t.id, t])),
    [teamsData],
  );

  const rows = useMemo(
    () =>
      results.map((r) =>
        mapSummaryToPayoutBreakdownRow(r, { usersById, teamsById }),
      ),
    [results, usersById, teamsById],
  );

  const isLoading = isLoadingData || isLoadingUsers || isLoadingTeams;

  return {
    rows,
    loading: isLoading,
    totalCount: summaryData?.totalCount,
    rowCount: results.length,
    paginationModel,
    setPaginationModel,
  };
}
